import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material'
import { Container } from 'react-bootstrap'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { FormInputText } from 'srs.sharedcomponents/lib/esm/components/organisms/FormInputText/FormInputText'
import { useTranslate } from 'srs.sharedcomponents/lib/esm/hooks/useTranslate'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  useAppDispatch,
  useAppSelector,
} from 'srs.sharedcomponents/lib/esm/redux/hooks'
import {
  clearLoanCartAll,
  loancartId,
  selectLoanCartItems,
} from 'srs.sharedcomponents/lib/esm/redux/slices/loanCartSlice'
import { ISubmitRentalOrderRequest } from 'srs.sharedcomponents/lib/esm/models/reguests/checkout/srs.RentalOrderRequestForm.model'
import { submitRentalOrder } from 'srs.sharedcomponents/lib/esm/redux/slices/checkoutSlice'
import LoadingIndicator from 'srs.sharedcomponents/lib/esm/components/organisms/LoadingIndicator/LoadingIndicator'
import { IsValidPhone } from 'srs.sharedcomponents/lib/esm/utils/constants'
interface LoanFormData {
  firstName: string
  lastName: string
  company: string
  email: string
  phone: string
  purchaseOrder: string
  repairOrder: string
  vin: string
  shipType: string
  notes: string
  vehicleServiceDate: string
  esstsRequiredDate: string
  esstsReturnDate: string
}

const LoanRequestForm = () => {
  const defaultValues = {
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    purchaseOrder: '',
    repairOrder: '',
    vin: '',
    shipType: 'Normal',
    notes: '',
    vehicleServiceDate: '',
    esstsRequiredDate: '',
    esstsReturnDate: '',
  }
  const { translate } = useTranslate()
  const dispatch = useAppDispatch()
  const history = useHistory()
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const CartID = useAppSelector(loancartId)
  const loanCartItems = useAppSelector(selectLoanCartItems)
  const schema = yup.object().shape({
    firstName: yup.string().required(translate('rfvFirstName_RentalCart')),
    lastName: yup.string().required(translate('rfvLastName_RentalCart')),
    company: yup.string().required(translate('rfvCompany_RentalCart')),
    email: yup
      .string()
      .required(translate('rfvEmail_RentalCart'))
      .email(translate('CreateUserInvalidEmailValidatorErrorMsg1')),
    phone: yup
      .string()
      .required(translate('rfvPhone_RentalCart'))
      .matches(IsValidPhone, translate('valInvalidShippingPhone')),
    purchaseOrder: yup.string().required(translate('rfvPO_RentalCart')),
    repairOrder: yup.string().required(translate('rfvRO_RentalCart')),
    vin: yup.string().required(translate('rfvVIN_RentalCart')),
    shipType: yup.string().required(),
    notes: yup.string().max(200, translate('valRentalShippingNote')),
    vehicleServiceDate: yup
      .date()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === '' ? null : value
      })
      .required(translate('rfvServiceDate_RentalCart'))
      .test('year-digits', translate('invalidDate'), function (value) {
        if (!value) return true
        const year = value.getFullYear()
        return year.toString().length <= 4
      }),

    esstsRequiredDate: yup
      .date()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === '' ? null : value
      })
      .required(translate('rfvESSTReqDate_RentalCart'))
      .min(new Date(), translate('rfcESSTReqDate_RentalCart'))
      .test('year-digits', translate('invalidDate'), function (value) {
        if (!value) return true
        const year = value.getFullYear()
        return year.toString().length <= 4
      }),

    esstsReturnDate: yup
      .date()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === '' ? null : value
      })
      .required(translate('rfvESSTRetDate_RentalCart'))
      .test(
        'is-after-required',
        translate('rfcESSTRetDate_RentalCart'),
        function (value) {
          const { esstsRequiredDate } = this.parent
          return value && esstsRequiredDate && value > esstsRequiredDate
        },
      )
      .test('year-digits', translate('invalidDate'), function (value) {
        if (!value) return true
        const year = value.getFullYear()
        return year.toString().length <= 4
      }),
  })
  const { handleSubmit, control, reset, setValue, watch, clearErrors } =
    useForm<LoanFormData>({
      defaultValues: defaultValues,
      resolver: yupResolver(schema),
      mode: 'all',
    })

  const watchedRequiredDate = watch('esstsRequiredDate')

  const getReturnDateLimits = () => {
    if (!watchedRequiredDate) return { min: undefined, max: undefined }

    const requiredDate = new Date(watchedRequiredDate)
    const minDate = new Date(requiredDate)
    minDate.setDate(minDate.getDate() + 1)

    const maxDate = new Date(requiredDate)
    maxDate.setDate(maxDate.getDate() + 21)

    return {
      min: minDate.toISOString().split('T')[0],
      max: maxDate.toISOString().split('T')[0],
    }
  }

  const returnDateLimits = getReturnDateLimits()

  useEffect(() => {
    setValue('esstsReturnDate', '')
    clearErrors('esstsReturnDate')
  }, [watchedRequiredDate, setValue])

  const onSubmit = (data: LoanFormData) => {
    setIsLoading(true)
    const payload: ISubmitRentalOrderRequest = {
      RentalOrderID: 0,
      FirstName: data.firstName,
      LastName: data.lastName,
      CompanyName: data.company,
      Email: data.email,
      PhoneNumber: data.phone,
      PurchaseOrderNumber: data.purchaseOrder,
      RepairOrderNumber: data.repairOrder,
      VIN: data.vin,
      RepairDate: new Date(data.vehicleServiceDate).toISOString(),
      RequiredDate: new Date(data.esstsRequiredDate).toISOString(),
      ReturnDate: new Date(data.esstsReturnDate).toISOString(),
      Notes: data.notes,
      ShippingType: data.shipType,
      PaymentMethodID: undefined,
      CartID: CartID || '',
    }

    dispatch(submitRentalOrder({ request: payload }))
      .then((response: any) => {
        if (response.meta.requestStatus === 'fulfilled') {
          setIsSubmissionSuccessful(true)
          dispatch(clearLoanCartAll())
        }
      })
      .finally(() => {
        setIsLoading(false)
        reset()
      })
  }
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
    }
  }

  const handleContinue = () => {
    history.push('/')
  }

  useEffect(() => {
    if (loanCartItems.length === 0 && !isSubmissionSuccessful) {
      history.push('/loancart', { from: 'checkout page' })
    }
  }, [loanCartItems, isSubmissionSuccessful])

  if (isLoading) {
    return <LoadingIndicator />
  }

  if (isSubmissionSuccessful) {
    return (
      <Container>
        <main id="main" role="main" className="pb-3 pt-3">
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="270px"
            bgcolor="white"
            style={{
              boxShadow: '0px 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
            }}
          >
            <Paper
              sx={{
                width: '100%',
                maxWidth: 1000,
                textAlign: 'center',
                padding: '20px',
                borderRadius: '8px',
                backgroundColor: '#EDF0F4',
                margin: 4,
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/Checkmark_Icon.svg`}
                alt="Home"
              />

              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {translate('lblTitle_RentalSubmission')}
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={3}>
                {translate('lblTitle_RentalSubmissionText')}
              </Typography>

              <Button
                sx={{
                  textTransform: 'none',
                  borderRadius: '20px',
                  padding: '8px 30px',
                  fontWeight: 500,
                  backgroundColor: '#0d6efd',
                  color: '#fff',
                }}
                onClick={handleContinue}
                id="continueButton"
              >
                {translate('btnContinueText')}
              </Button>
            </Paper>
          </Box>
        </main>
      </Container>
    )
  }

  return (
    <Container>
      <main id="main" role="main" className="pb-3 pt-3">
        <Typography variant="h5" fontWeight={600} mb={2}>
          {translate('lblTitle_RentalCart')}
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Paper sx={{ p: 3 }}>
            <Typography fontWeight={600} mb={2}>
              {translate('lblSubTitle_RentalCart')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormInputText
                  id="firstName"
                  name="firstName"
                  control={control}
                  schema={schema}
                  label={translate('lblFirstName_RentalCart')}
                  onKeyDown={handleKeyDown}
                  removeInput={() => {
                    setValue('firstName', '')
                    clearErrors('firstName')
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormInputText
                  id="lastName"
                  name="lastName"
                  control={control}
                  schema={schema}
                  label={translate('lblLastName_RentalCart')}
                  onKeyDown={handleKeyDown}
                  removeInput={() => {
                    setValue('lastName', '')
                    clearErrors('lastName')
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormInputText
                  id="company"
                  name="company"
                  control={control}
                  schema={schema}
                  label={translate('lblCompany_RentalCart')}
                  onKeyDown={handleKeyDown}
                  removeInput={() => {
                    setValue('company', '')
                    clearErrors('company')
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormInputText
                  id="email"
                  name="email"
                  control={control}
                  schema={schema}
                  label={translate('lblEmail_RentalCart')}
                  additionalInfo={translate(`txtEmailDescription`)}
                  onKeyDown={handleKeyDown}
                  removeInput={() => {
                    setValue('email', '')
                    clearErrors('email')
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormInputText
                  id="phone"
                  name="phone"
                  control={control}
                  schema={schema}
                  label={translate('lblPhone_RentalCart')}
                  allowSpecialCharacters
                  onKeyDown={handleKeyDown}
                  removeInput={() => {
                    setValue('phone', '')
                    clearErrors('phone')
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormInputText
                  id="purchaseOrder"
                  name="purchaseOrder"
                  control={control}
                  schema={schema}
                  label={translate('lblPO_RentalCart')}
                  onKeyDown={handleKeyDown}
                  removeInput={() => {
                    setValue('purchaseOrder', '')
                    clearErrors('purchaseOrder')
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormInputText
                  id="repairOrder"
                  name="repairOrder"
                  control={control}
                  schema={schema}
                  label={translate('lblRO_RentalCart')}
                  onKeyDown={handleKeyDown}
                  removeInput={() => {
                    setValue('repairOrder', '')
                    clearErrors('repairOrder')
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormInputText
                  id="vin"
                  name="vin"
                  control={control}
                  schema={schema}
                  label={translate('lblVIN_RentalCart')}
                  maxCharacters={17}
                  onKeyDown={handleKeyDown}
                  removeInput={() => {
                    setValue('vin', '')
                    clearErrors('vin')
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography fontWeight={700}>
                    {translate('lblShip_RentalCart')}
                  </Typography>
                  <Controller
                    name="shipType"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup row {...field}>
                        <FormControlLabel
                          value="Normal"
                          control={<Radio />}
                          label={translate('rbShippingOption1')}
                        />
                        <FormControlLabel
                          value="Overnight"
                          control={<Radio />}
                          label={translate('rbShippingOption2')}
                        />
                      </RadioGroup>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormInputText
                  id="notes"
                  name="notes"
                  control={control}
                  schema={schema}
                  label={translate('shippingNoteHeader_RentalCart')}
                  multiline={2}
                  onKeyDown={handleKeyDown}
                  removeInput={() => {
                    setValue('notes', '')
                    clearErrors('notes')
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormInputText
                  id="vehicleServiceDate"
                  name="vehicleServiceDate"
                  control={control}
                  schema={schema}
                  label={translate('lblServicedate_RentalCart')}
                  type="date"
                  onKeyDown={handleKeyDown}
                  removeInput={() => {
                    setValue('vehicleServiceDate', '')
                    clearErrors('vehicleServiceDate')
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormInputText
                  id="esstsRequiredDate"
                  name="esstsRequiredDate"
                  control={control}
                  schema={schema}
                  label={translate('lblESSTReqDate_RentalCart')}
                  type="date"
                  onKeyDown={handleKeyDown}
                  removeInput={() => {
                    setValue('esstsRequiredDate', '')
                    clearErrors('esstsRequiredDate')
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormInputText
                  id="esstsReturnDate"
                  name="esstsReturnDate"
                  control={control}
                  schema={schema}
                  label={translate('lblESSTRetDate_RentalCart')}
                  type="date"
                  min={returnDateLimits.min}
                  max={returnDateLimits.max}
                  onKeyDown={handleKeyDown}
                  removeInput={() => {
                    setValue('esstsReturnDate', '')
                    clearErrors('esstsReturnDate')
                  }}
                />
              </Grid>
            </Grid>

            <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
              <Button onClick={() => reset()} id="resetLoanRequestButton">
                {translate('btnResetTooltip')}
              </Button>
              <Button
                variant="outlined"
                style={{ borderRadius: '20px' }}
                id="cancelLoanRequestButton"
                onClick={() => history.push('/loancart')}
              >
                {translate('btnCancelText')}
              </Button>
              <Button
                type="submit"
                onClick={handleSubmit(onSubmit)}
                sx={{
                  textTransform: 'none',
                  borderRadius: '20px',
                  padding: '8px 30px',
                  fontWeight: 500,
                  backgroundColor: '#0d6efd',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#0b5ed7',
                    color: '#fff',
                  },
                }}
                id="submitLoanRequestButton"
              >
                {translate('lblSubmit_RentalCart')}
              </Button>
            </Box>
          </Paper>
        </form>
      </main>
    </Container>
  )
}

export default LoanRequestForm
